import styled from 'styled-components';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const StyledModalHeader = styled.h2`
    color: #292561;
    font-size: 26px; /* Reduced font size */
    font-weight: 700;
    text-align: left;
    margin-top: 10px;
    margin-bottom: 15px; /* Reduced bottom margin */
`;

const StyledModalBody = styled.div`
    font-family: Montserrat, sans-serif;
    background-color: #ffffff;
    padding: 15px; /* Reduced padding */
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: start;
    text-align: left;
`;

const InfoSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr; /* Two columns: labels and values */
    gap: 5px 20px; /* Adds space between rows and columns */
    width: 100%;
`;

const InfoText = styled.p`
    font-size: 16px;
    font-weight: 500;
    color: #292561;
    margin: 0;
    padding-top: 10px;
`;

const LabelText = styled(InfoText)`
    font-weight: 600;
`;

const AmountSection = styled.div`
    margin-top: -20px;
    display: flex;
    flex-direction: column;
    margin-left: 154px;
    width: calc(100% - 180px);

    @media (min-width: 300px) and (max-width: 500px) {
        margin-left: 115px;
        width: calc(100% - 120px);
    }
`;

const BreakdownText = styled.p`
    font-size: 14px;
    font-weight: 500;
    color: #292561;
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
`;

const BreakdownText2 = styled.p`
    font-size: 15px;
    font-weight: 800;
    color: #292561;
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
    font-text: bold;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 15px;
`;

const GoBackButton = styled.button`
    background-color: #fff;
    color: #292561;
    padding: 5px 10px;
    font-size: 14px;
    border: 2px solid #292561;
    border-radius: 40px;
    cursor: pointer;
    font-weight: 600;

    &:hover {
        background-color: #f1f3f5;
    }
`;

const ProceedButton = styled.button`
    background-color: #b01116;
    color: white;
    padding: 8px 20px;
    font-size: 14px;
    border: none;
    border-radius: 40px;
    cursor: pointer;
    font-weight: 600;

    &:hover {
        background-color: #b71c1c;
    }
`;

const MySwal = withReactContent(Swal);

export default function breakDownModal({ handleClientSelection, handlePlan, plan, formData, claimsData }) {
    const expiryDate = new Date(claimsData.policy?.policy?.expiryDate);

    const { firstName, middleName, lastName, street, barangay, city, province, zipCode } = formData;
    const name = `${firstName}${middleName ? ' ' + middleName + ' ' : ' '}${lastName}`;
    const fullAddress = `${street}, ${barangay}, ${city}, ${province}, ${zipCode}`;

    const formatDate = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const policyPeriod = getPolicyPeriod(expiryDate);

    function getPolicyPeriod(expiryDate) {
        const today = new Date();
        const expiryDateLocal = new Date(expiryDate.getTime() + today.getTimezoneOffset() * 60000);
    
        // Check if expiryDate is today or in the past
        if (!expiryDate || expiryDateLocal <= today) {
            // Start the policy period from tomorrow
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            
            const endDate = new Date(tomorrow);
            endDate.setFullYear(tomorrow.getFullYear() + 1);
            return `${formatDate(tomorrow)} 04:00PM - ${formatDate(endDate)} 04:00PM`;
        } else {
            // If expiryDate is in the future
            const endDate = new Date(expiryDateLocal);
            endDate.setFullYear(expiryDateLocal.getFullYear() + 1);
            return `${formatDate(expiryDateLocal)} 04:00PM - ${formatDate(endDate)} 04:00PM`;
        }
    }
    
    return MySwal.fire({
        html: (
            <StyledModalBody>
                <StyledModalHeader>Review Booking</StyledModalHeader>
                <InfoSection>
                    <LabelText>Name</LabelText>
                    <InfoText>{name}</InfoText>

                    <LabelText>Policy Period</LabelText>
                    <InfoText>{policyPeriod}</InfoText>

                    <LabelText>Policy Limit</LabelText>
                    <InfoText>Max PHP 50,000.00 annual aggregate</InfoText>

                    <LabelText>Location of Risk</LabelText>
                    <InfoText>{fullAddress}</InfoText>

                    <LabelText>Premium</LabelText>
                </InfoSection>

                <AmountSection>
                    <BreakdownText>
                        Basic Premium <span>{plan.basePrem.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText>
                        VAT <span>{plan.vatAmt.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText>
                        DST <span>{plan.dstAmt.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText>
                        FST <span>{plan.fstAmt.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText>
                        LGT <span>{plan.lgtAmt.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText>
                        Other <span>{plan.other.toFixed(2)}</span>
                    </BreakdownText>
                    <BreakdownText2>
                        Total Amount Due <span style={{ fontSize: '13px'}}>{plan.premAmt.toFixed(2)}</span>
                    </BreakdownText2>
                </AmountSection>

                <ButtonContainer>
                    <GoBackButton
                        onClick={() => {
                            MySwal.close();
                        }}
                    >
                        GO BACK & MODIFY DETAILS
                    </GoBackButton>
                    <ProceedButton onClick={() => handleClientSelection()}>PROCEED TO PAYMENT</ProceedButton>
                </ButtonContainer>
            </StyledModalBody>
        ),
        showConfirmButton: false,
        showCloseButton: true,
        customClass: {
            popup: 'my-modal'
        },
        backdrop: `rgba(159, 159, 159, 0.5)`,
        allowOutsideClick: false,
        allowEscapeKey: false
    });
}
